import React from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaArchive, FaPlus, FaRegFile, FaTrashAlt, FaFileUpload } from "react-icons/fa";
import { U3146ROLES } from "../../../u3146ProjectClose/U3146";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000GridStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { NS, U3121ROLES } from "../../U3121";

interface U3121ProjectListToolBarProps<T extends Record<string, unknown>> {
  gridStateHandlers: U2000GridStateHandlers<T>;
  handlerActions?: U2000TableHandlers<T> & { onCloseInactiveProjects?: () => void };
}

export const U3121ProjectListToolBar: React.FC<U3121ProjectListToolBarProps<Record<string, unknown>>> = ({
  gridStateHandlers,
  handlerActions,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const selectedRows = gridStateHandlers.getSelectedRows();
  const disableUpdateBtn = selectedRows.length !== 1;
  const disableDeleteBtn = selectedRows.length === 0;

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3121ROLES.ADD),
      handleClick: handlerActions?.onAdd,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "uploadFromFile",
      label: t("btnUploadProjectFromFile", { ns: NS }),
      hasRole: hasRole(U3121ROLES.ADD),
      handleClick: (handlerActions as { onUploadFromFile?: () => void })?.onUploadFromFile,
      icon: FaFileUpload,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3121ROLES.EDIT),
      handleClick: handlerActions?.onModify,
      disabled: disableUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3121ROLES.DELETE),
      handleClick: handlerActions?.onDelete,
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "closeInactive",
      label: t("btnCloseInactiveProjects", { ns: NS }),
      hasRole: hasRole(U3146ROLES.READ),
      handleClick: handlerActions?.onCloseInactiveProjects,
      icon: FaArchive,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
